import React from "react";
import { Row, Col, Tab, Tabs } from "react-bootstrap";
import FormState from "shared/FormState";
import ErrorList from "shared/components/ErrorList";
import axios from "axios";
import { redirectTo } from "shared/utils/redirect";
import routes from "startups/routes";

import { booleanTransformator } from "shared/transformators";
import {
  checkboxValidator,
  dateValidator,
  emailValidator,
  passwordValidator,
  phoneValidator,
} from "shared/validators";
import RegistrationFormProgressBar from "../RegistrationFormProgressBar";
import TosForm from "../TosForm";
import PersonalInformationForm from "../PersonalInformationForm";
import CompanyForm from "../CompanyForm";

export default class RegistrationForm extends React.Component {
  constructor(props) {
    super(props);

    this.state = {
      activeTabKey: 1,
      formState: this.setupFormState(),
      errorMessages: [],
      isLoading: false,
    };
  }

  setupFormState() {
    return Object.create(FormState).init({
      attributeMapping: {
        edeka_store_count: {},
        email: { validator: emailValidator.isValid },
        first_name: {},
        founding_date: { validator: dateValidator.isValid },
        last_name: {},
        last_year_gross_revenue: {},
        main_product_category: {},
        main_product_name: {},
        name: {},
        password: { validator: passwordValidator.isValid },
        phone: { validator: phoneValidator.isValid },
        phone_country_code: {},
        privacy_statement_accepted: {
          validator: checkboxValidator.isValid,
          transformator: booleanTransformator,
        },
        salutation: {},
        subscribed_newsletter: {
          validator: checkboxValidator.isValidAndOptional,
          transformator: booleanTransformator,
        },
        tos_accepted: {
          validator: checkboxValidator.isValid,
          transformator: booleanTransformator,
        },
      },
      model: {
        phone: "+49",
        phone_country_code: "DE",
        salutation: "Frau",
        privacy_statement_accepted: false,
        tos_accepted: false,
        subscribed_newsletter: false,
      },
      groups: {
        personalInformation: [
          "email",
          "password",
          "first_name",
          "last_name",
          "phone",
          "salutation",
          "phone_country_code",
        ],
        company: [
          "edeka_store_count",
          "founding_date",
          "last_year_gross_revenue",
          "main_product_category",
          "main_product_name",
          "name",
        ],
        tos: [
          "tos_accepted",
          "privacy_statement_accepted",
          "subscribed_newsletter",
        ],
      },
      renderCallback: this.rerender.bind(this),
    });
  }

  setGeneralError(message = "Bitte fülle alle Felder vollständig aus.") {
    const errorMessages = [message];

    this.setState({ errorMessages });
  }

  moveToNextTab() {
    let activeTabKey = this.state.activeTabKey + 1;
    if (activeTabKey > 3) {
      activeTabKey = 3;
    }
    this.setState({ activeTabKey, errorMessages: [] });
  }

  moveToPrevTab() {
    let activeTabKey = this.state.activeTabKey - 1;
    if (activeTabKey < 0) {
      activeTabKey = 0;
    }
    this.setState({ activeTabKey });
  }

  // FormState requires a way to notify react to update the view
  // Avoiding forceUpdate to re-render, see https://reactjs.org/docs/react-component.html#forceupdate
  rerender() {
    this.setState({ formState: this.state.formState });
  }

  disableSelectTab(key) {
    this.setState({ activeTabKey: key });
  }

  validateGroupAndMoveToNextTab(groupName) {
    if (this.state.formState.clientValidateGroup(groupName)) {
      this.moveToNextTab();
    } else {
      this.setGeneralError();
    }
  }

  validateAndMoveToStepTwo(event) {
    event.preventDefault();
    this.validateGroupAndMoveToNextTab("personalInformation");
  }

  validateAndMoveToStepThree(event) {
    event.preventDefault();
    this.validateGroupAndMoveToNextTab("company");
  }

  submitForm() {
    const { formState } = this.state;
    if (!formState.clientValidateGroup("tos")) {
      this.setGeneralError(
        "Bitte akzeptiere die Datenschutzerklärung und " +
          "die Allgemeinen Geschäftsbedingungen!",
      );
      return;
    }

    const requestData = Object.keys(formState.groups).reduce(
      (accumulator, groupName) => {
        return {
          ...accumulator,
          [groupName]: formState.readModelGroup(groupName),
        };
      },
      {},
    );

    // Note: Doing manual request here, because backend does not yet follow
    //       new json-api guidelines
    this.setState({ isLoading: true }, () => {
      axios
        .post(routes.registerStartupPath(), requestData)
        .then(startupAccountData => {
          this.setState({ isLoading: false }, () => {
            redirectTo(
              routes.startupAccountsConfirmationSentPath({
                confirmation_email: startupAccountData.data.email,
              }),
            );
          });
        })
        .catch(errorResponse => {
          this.setState({ isLoading: false }, () => {
            if (
              errorResponse.response &&
              errorResponse.response.status === 422
            ) {
              const errorMessages = [];
              const errorAttributeMapping = [];
              Object.keys(errorResponse.response.data).forEach(
                attributeName => {
                  errorResponse.response.data[attributeName].forEach(
                    message => {
                      errorMessages.push(message);
                      errorAttributeMapping.push({
                        attribute: attributeName,
                      });
                    },
                  );
                },
              );

              this.setState(
                {
                  errorMessages,
                  // TODO This could be improved: Go to respective page
                  // which has the error
                  activeTabKey: 1,
                },
                () => {
                  formState.updateServerValidation(errorAttributeMapping);
                },
              );
            } else {
              this.setGeneralError(
                "Leider ist ein interner Fehler aufgetreten. " +
                  "Die Registrierung war nicht erfolgreich. " +
                  "Bitte kontaktiere das EDEKA StartHub Team - " +
                  "info@starthub.edeka",
              );
            }
          });
        });
    });
  }

  render() {
    const { state } = this;
    const { formState } = state;

    return (
      <Row className="registration-form">
        <Col xs={12} md={6} mdOffset={3}>
          <h1>Registrierung für Startups</h1>

          <p>
            Um Zugang zu EDEKA StartHub zu erhalten, ist eine kurze Registrierung
            nötig.
          </p>

          <RegistrationFormProgressBar step={this.state.activeTabKey} />

          <Tabs
            id="registration-tabs"
            className="tabs"
            activeKey={this.state.activeTabKey}
            onSelect={this.disableSelectTab.bind(this)}
          >
            <ErrorList errorMessages={this.state.errorMessages} />

            <Tab eventKey={1}>
              <PersonalInformationForm
                formState={formState}
                submitFn={this.validateAndMoveToStepTwo.bind(this)}
              />
            </Tab>
            <Tab eventKey={2}>
              <CompanyForm
                formState={formState}
                submitFn={this.validateAndMoveToStepThree.bind(this)}
                cancelFn={this.moveToPrevTab.bind(this)}
                allProductCategories={this.props.allProductCategories}
              />
            </Tab>
            <Tab eventKey={3}>
              <TosForm
                formState={formState}
                submitFn={this.submitForm.bind(this)}
                cancelFn={this.moveToPrevTab.bind(this)}
                privacyUrl={this.props.privacyUrl}
                isLoading={this.state.isLoading}
                tosUrl={this.props.tosUrl}
              />
            </Tab>
          </Tabs>
        </Col>
      </Row>
    );
  }
}
